import { useState } from 'react';
import type { KeyedMutator } from 'swr';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { EngineService } from '@/services/engine';
import type {
    ReportEvent,
    ReportEventOrderReference,
    ReportWithEvents,
    ReorderReportEventPayload,
} from '@/types/report';
import {
    type ReportEventSortOption,
    type ReportGroupByMode,
} from '../constants';
import { moveReportEvent } from '../utils/moveReportEvent';
import { sortReportEvents } from '../utils/sortReportEvents';
import type {
    ReportOrderMessages,
    ReportOrderMutation,
} from './useReportOrderMutation';

interface ReportEventMove {
    source: ReportEventOrderReference;
    target: ReportEventOrderReference;
    placement: 'before' | 'after';
}

export interface ReportEventReordering {
    isManualOrder: boolean;
    isPending: boolean;
    request: (
        sourceEvent: ReportEvent,
        targetEvent: ReportEvent,
        placement: 'before' | 'after',
    ) => void;
}

interface UseReportEventOrderingParams {
    reportId: string;
    mutate: KeyedMutator<ReportWithEvents>;
    groupBy: ReportGroupByMode;
    sortOption: ReportEventSortOption;
    setSortOption: (option: ReportEventSortOption) => void;
    orderMutation: ReportOrderMutation;
}

export function useReportEventOrdering({
    reportId,
    mutate,
    groupBy,
    sortOption,
    setSortOption,
    orderMutation,
}: UseReportEventOrderingParams) {
    const { t } = useTranslation(I18nNamespace.REPORTS);
    const [pendingMove, setPendingMove] = useState<ReportEventMove>();
    const isManualOrder = sortOption.field === 'manual';

    const messages: ReportOrderMessages = {
        success: t('detail.reorder.success'),
        stale: t('detail.reorder.stale'),
        error: t('detail.reorder.error'),
    };

    // The saved order already lives on the backend, so only a sorted view has to
    // report the order it is showing — the same list the events list renders.
    const buildPayload = (
        move: ReportEventMove,
        currentReport: ReportWithEvents,
    ): ReorderReportEventPayload => ({
        ...move,
        groupBy,
        currentOrder: isManualOrder
            ? undefined
            : sortReportEvents(currentReport.events, sortOption).map(
                  (event) => ({
                      id: event.id,
                      eventType: event.eventType,
                  }),
              ),
        expectedOrderVersion: currentReport.orderVersion,
    });

    const applyMove = (events: ReportEvent[], move: ReportEventMove) =>
        moveReportEvent(events, move.source, move.target, move.placement);

    const saveMoveWithinSavedOrder = (move: ReportEventMove) =>
        orderMutation.run(messages, async (currentReport) => {
            await mutate(
                async (cachedReport) => {
                    if (!cachedReport) {
                        return cachedReport;
                    }
                    const { orderVersion } =
                        await EngineService.reorderReportEvent(
                            reportId,
                            buildPayload(move, currentReport),
                        );
                    return {
                        ...cachedReport,
                        events: applyMove(cachedReport.events, move),
                        orderVersion,
                    };
                },
                {
                    optimisticData: {
                        ...currentReport,
                        events: applyMove(currentReport.events, move),
                    },
                    rollbackOnError: true,
                    revalidate: false,
                },
            );
        });

    const replaceSavedOrderWithCurrentSort = async () => {
        if (!pendingMove) {
            return;
        }
        const move = pendingMove;
        await orderMutation.run(messages, async (currentReport) => {
            await EngineService.reorderReportEvent(
                reportId,
                buildPayload(move, currentReport),
            );
            setSortOption({ field: 'manual', direction: 'asc' });
            await mutate();
        });
        // Closed on failure too: the move was rebased away by the refetch, so
        // leaving the dialog open would re-submit against a stale order.
        setPendingMove(undefined);
    };

    const request = (
        sourceEvent: ReportEvent,
        targetEvent: ReportEvent,
        placement: ReportEventMove['placement'],
    ) => {
        const move: ReportEventMove = {
            source: {
                id: sourceEvent.id,
                eventType: sourceEvent.eventType,
            },
            target: {
                id: targetEvent.id,
                eventType: targetEvent.eventType,
            },
            placement,
        };
        if (isManualOrder) {
            void saveMoveWithinSavedOrder(move);
            return;
        }
        setPendingMove(move);
    };

    return {
        reordering: {
            isManualOrder,
            isPending: orderMutation.isPending,
            request,
        } satisfies ReportEventReordering,
        isReordering: orderMutation.isPending,
        confirmationOpen: pendingMove !== undefined,
        confirmSortedReorder: replaceSavedOrderWithCurrentSort,
        cancelSortedReorder: () => setPendingMove(undefined),
    };
}
